import { useState, useEffect, useCallback } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { apiClient, InitData } from '../../core'
import { t, intlLocale } from '../../core/i18n'
import { studietsTidszone } from '../../core/tidszone'

interface ApiPayload {
  configured?: boolean
  water_temperature: number | null
  air_temperature: number | null
  last_updated: string | null
}

type Variant = 'dark' | 'ocean' | 'light'

interface Props {
  initData: InitData
  title?: string | null
  variant?: Variant
  showLastUpdated?: boolean
  /** Badested-koordinater fra attributterne. Sat → Open-Meteo i stedet for Thermit. */
  latitude?: string | null
  longitude?: string | null
}

// Thermit opdaterer typisk hvert kvarter. 20 minutter rammer det uden at
// hamre deres API — samme interval som CMS-udgaven af banneret.
const REFRESH_INTERVAL_MS = 20 * 60 * 1000

interface Palette {
  background: string
  textPrimary: string
  textMuted: string
  accent: string
  chip: string
  chipHover: string
}

// Delt palet med CMS-banneret (TemperatureBannerWidget.tsx) så embed- og
// CMS-fladen ser ens ud. Ændrer du den ene, så ret den anden.
const PALETTES: Record<Variant, Palette> = {
  ocean: {
    background: 'linear-gradient(135deg, #0f2940 0%, #133852 50%, #0e2438 100%)',
    textPrimary: '#ffffff',
    textMuted: 'rgba(226,240,253,0.7)',
    accent: '#5dd5ff',
    chip: 'rgba(255,255,255,0.08)',
    chipHover: 'rgba(255,255,255,0.16)',
  },
  light: {
    background: 'linear-gradient(135deg, #f7fafc 0%, #eef4f8 100%)',
    textPrimary: '#0b1f33',
    textMuted: 'rgba(11,31,51,0.6)',
    accent: '#d97706',
    chip: 'rgba(11,31,51,0.06)',
    chipHover: 'rgba(11,31,51,0.12)',
  },
  dark: {
    background: 'linear-gradient(135deg, #1a1d22 0%, #2c2c2c 100%)',
    textPrimary: '#ffffff',
    textMuted: 'rgba(255,255,255,0.55)',
    accent: '#F4B63D',
    chip: 'rgba(255,255,255,0.06)',
    chipHover: 'rgba(255,255,255,0.16)',
  },
}

/** Ét decimal, og «--» når måleren ikke har en værdi. */
function formatTemp(value: number | null, intl: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '--'
  return new Intl.NumberFormat(intl, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)
}

/** «1. okt., 07.05» — dato + klokkeslæt, så banneret matcher CMS-udgaven. I studiets tidszone (QA 27-10). */
function formatUpdated(iso: string | null, intl: string, timeZone: string): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const day = new Intl.DateTimeFormat(intl, { day: 'numeric', month: 'short', timeZone }).format(d)
  const time = new Intl.DateTimeFormat(intl, { hour: '2-digit', minute: '2-digit', timeZone }).format(d)
  return `${day}, ${time}`
}

/**
 * TemperatureWidget — vand- og lufttemperatur som et vandret banner.
 *
 * Henter fra `/api/widget/temperature`, som slår tenanten op på widget-API-nøglen.
 * Er hverken Thermit-sensor eller koordinater sat op (404 `configured: false`),
 * renderer widgeten ingenting i stedet for en tom ramme — så en forglemt opsætning
 * ikke efterlader et hul på kundens side.
 *
 * Designet deler palet og opbygning med CMS-banneret (TemperatureBannerWidget.tsx):
 * ikoner, gradient-baggrund, opdater-knap og dato-format, så de to flader er ens.
 */
export function TemperatureWidget({
  initData,
  title = null,
  variant = 'ocean',
  showLastUpdated = true,
  latitude = null,
  longitude = null,
}: Props) {
  const locale = initData?.locale || 'da'
  const s = t(locale)
  const intl = intlLocale(locale)

  const [data, setData] = useState<ApiPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [unconfigured, setUnconfigured] = useState(false)
  const [pulse, setPulse] = useState(false)

  const load = useCallback(async (force: boolean) => {
    if (force) setRefreshing(true)
    const params = new URLSearchParams()
    if (latitude) params.set('lat', latitude)
    if (longitude) params.set('lon', longitude)
    if (force) params.set('reload', '1')
    const qs = params.toString()
    const result = await apiClient.get<ApiPayload>(
      '/api/widget/temperature' + (qs ? `?${qs}` : ''),
    )
    if (result.data && result.data.configured !== false) {
      setData(result.data)
      setPulse(true)
      setTimeout(() => setPulse(false), 600)
    } else {
      // Ingen data: enten 404 `configured:false` (apiClient kasserer body på
      // non-2xx, så vi kan ikke aflæse feltet her) eller en hentefejl. En
      // offentlig embed-widget skal SKJULE sig i begge tilfælde, ikke vise en
      // fejl-banner til besøgende — samme adfærd som CMS-banneret (return null
      // på !res.ok).
      setUnconfigured(true)
    }
    setLoading(false)
    setRefreshing(false)
  }, [latitude, longitude])

  useEffect(() => {
    load(false)
    const id = setInterval(() => load(false), REFRESH_INTERVAL_MS)
    return () => clearInterval(id)
  }, [load])

  // Hverken Thermit-opsætning eller koordinater → vis intet.
  if (unconfigured) return null

  const p = PALETTES[variant] || PALETTES.ocean
  const skeleton = loading && !data
  const updated = showLastUpdated
    ? (skeleton ? '…' : (formatUpdated(data?.last_updated ?? null, intl, studietsTidszone(initData?.timezone)) ?? '--'))
    : null

  return (
    <div>
      <style>{styles}</style>
      <div class="hs-temp" style={{ background: p.background, color: p.textPrimary }}>
        {variant === 'ocean' && <div class="hs-temp-glow" aria-hidden="true" />}

        <div class="hs-temp-inner">
          <div class="hs-temp-readings">
            {title && (
              <div class="hs-temp-title" style={{ color: p.textMuted }}>{title}</div>
            )}

            <Reading
              icon={<WaveIcon color={p.accent} />}
              label={s.temperatureWater}
              value={skeleton ? null : (data?.water_temperature ?? null)}
              palette={p}
              intl={intl}
              pulse={pulse}
            />

            <div class="hs-temp-divider" style={{ background: p.textMuted }} />

            <Reading
              icon={<SunIcon color={p.accent} />}
              label={s.temperatureAir}
              value={skeleton ? null : (data?.air_temperature ?? null)}
              palette={p}
              intl={intl}
              pulse={pulse}
            />
          </div>

          {showLastUpdated && (
            <div class="hs-temp-meta">
              {updated && (
                <div class="hs-temp-updated" style={{ color: p.textMuted }}>
                  {s.temperatureUpdated}
                  <div class="hs-temp-updated-value" style={{ color: p.textPrimary }}>{updated}</div>
                </div>
              )}
              <button
                type="button"
                class="hs-temp-refresh"
                onClick={() => load(true)}
                disabled={refreshing}
                aria-label={s.temperatureUpdated}
                style={{ background: p.chip, color: p.textPrimary, cursor: refreshing ? 'wait' : 'pointer' }}
              >
                <RefreshIcon spinning={refreshing} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Reading({
  icon,
  label,
  value,
  palette,
  intl,
  pulse,
}: {
  icon: ComponentChildren
  label: string
  value: number | null
  palette: Palette
  intl: string
  pulse: boolean
}) {
  const empty = value === null || value === undefined || Number.isNaN(value)
  return (
    <div class="hs-temp-reading">
      <div class="hs-temp-icon">{icon}</div>
      <div>
        <div class="hs-temp-label" style={{ color: palette.textMuted }}>{label}</div>
        <div class="hs-temp-value-row" style={{ transform: pulse ? 'scale(1.04)' : 'scale(1)' }}>
          {empty ? (
            <span class="hs-temp-value" style={{ color: palette.textMuted }}>–</span>
          ) : (
            <>
              <span class="hs-temp-value" style={{ color: palette.accent }}>{formatTemp(value, intl)}</span>
              <span class="hs-temp-unit">°C</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function WaveIcon({ color }: { color: string }) {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2 12c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" />
      <path d="M2 17c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" opacity="0.55" />
      <path d="M2 7c2 0 2-2 4-2s2 2 4 2 2-2 4-2 2 2 4 2 2-2 4-2" opacity="0.3" />
    </svg>
  )
}

function SunIcon({ color }: { color: string }) {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={color} stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  )
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={spinning ? 'hs-temp-spin' : undefined}
    >
      <path d="M21 12a9 9 0 0 1-15.5 6.3L2 16" />
      <path d="M3 12a9 9 0 0 1 15.5-6.3L22 8" />
      <path d="M22 4v4h-4M2 20v-4h4" />
    </svg>
  )
}

const styles = `
.hs-temp {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  padding: 20px 24px;
  box-shadow: 0 10px 30px -10px rgba(0,0,0,0.35);
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}
.hs-temp-glow {
  position: absolute;
  inset: 0;
  opacity: 0.18;
  pointer-events: none;
  background:
    radial-gradient(60% 80% at 80% 20%, rgba(93,213,255,0.5), transparent 60%),
    radial-gradient(50% 70% at 10% 100%, rgba(244,182,61,0.35), transparent 60%);
}
.hs-temp-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.hs-temp-readings {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.hs-temp-title {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-right: 4px;
}
.hs-temp-reading { display: flex; align-items: center; gap: 12px; }
.hs-temp-icon { flex-shrink: 0; line-height: 0; }
.hs-temp-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.hs-temp-value-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-variant-numeric: tabular-nums;
  transition: transform 250ms ease;
}
.hs-temp-value { font-size: 28px; font-weight: 700; line-height: 1; }
.hs-temp-unit { font-size: 14px; font-weight: 600; }
.hs-temp-divider { width: 1px; height: 32px; opacity: 0.35; }
.hs-temp-meta { display: flex; align-items: center; gap: 12px; }
.hs-temp-updated {
  font-size: 11px;
  letter-spacing: 0.05em;
  text-align: right;
}
.hs-temp-updated-value { font-size: 12px; font-variant-numeric: tabular-nums; margin-top: 2px; }
.hs-temp-refresh {
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms ease, transform 150ms ease;
}
.hs-temp-refresh:hover:not(:disabled) { transform: rotate(-20deg); }
.hs-temp-refresh:disabled { opacity: 0.7; }
.hs-temp-spin { animation: hs-temp-spin 0.9s linear infinite; transform-origin: 50% 50%; }
@keyframes hs-temp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@media (max-width: 520px) {
  .hs-temp-inner { justify-content: center; }
  .hs-temp-readings { justify-content: center; }
}
`
